import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { PalmettoDataGrid } from '@zawarski/palmetto-typescript-data-grid';
import type { PalmettoGridColumn } from '@zawarski/palmetto-typescript-data-grid';
import '@zawarski/palmetto-typescript-data-grid/styles.css';
import { ArrowLeft } from 'lucide-react';
import MobileCard, { CardMeta, CardRow, CardTitle, MOBILE_CARD_BAND } from '@components/MobileCard';
import { Fab, IconButton } from '@components/rep';
import { useAccess } from '@hooks/useAccess';
import { useRealtimeSubscription } from '@hooks/useRealtime';
import type { NotificationContact } from '@/types';
import type { NotificationDomain } from './domain';
import { useNotificationDomain } from './useNotificationDomain';

const methodsOf = (row: NotificationContact) =>
  [
    Number(row.pvMethodOfContactText) === 1 ? 'Text message' : '',
    Number(row.pvMethodOfContactEmail) === 1 ? 'Email' : '',
  ]
    .filter(Boolean)
    .join(', ');

const COLUMNS: PalmettoGridColumn[] = [
  { field: 'pvFullName', headerName: 'Contact name', enabled: true },
  { field: 'pvPhoneNumber', headerName: 'Phone number', enabled: true },
  { field: 'pvEmail', headerName: 'Email Address', enabled: true },
  {
    field: 'pvMethodsOfContact',
    headerName: 'Methods of contact',
    enabled: true,
    valueGetter: (_v, row) => methodsOf(row as NotificationContact),
  },
  {
    field: 'triggers',
    headerName: 'Triggers',
    enabled: true,
    valueGetter: (value: string[] | undefined) => (value || []).join(', '),
  },
];

const MobileRow = (datum: { row?: Record<string, unknown>; rowId: unknown }) => {
  const row = (datum.row || {}) as Partial<NotificationContact>;
  return (
    <MobileCard loaded={!!row.pvDataID}>
      <CardTitle>{row.pvFullName}</CardTitle>
      <CardRow>
        <CardMeta>{row.pvEmail || row.pvPhoneNumber || ''}</CardMeta>
        <CardMeta>{methodsOf(row as NotificationContact)}</CardMeta>
      </CardRow>
      <CardMeta>{(row.triggers || []).join(', ')}</CardMeta>
    </MobileCard>
  );
};

/** Contact catalog for one domain (client-side rows; the API returns the full list). */
const NotificationsList = ({ domain }: { domain: NotificationDomain }) => {
  const navigate = useNavigate();
  const { isStateUser, canEdit } = useAccess();
  const d = useNotificationDomain(domain);

  // Realtime: contacts are a small catalog, so any event reloads the list.
  const refetchList = d.refetchList;
  const onEvent = useCallback(() => void refetchList(), [refetchList]);
  useRealtimeSubscription(d.config.channel, onEvent);

  useEffect(() => {
    document.title = `${d.config.title} - Palmetto REP`;
  }, [d.config.title]);

  const canWrite = canEdit && (domain === 'equipment' || isStateUser);

  return (
    <div className='rep:relative rep:flex rep:h-full rep:w-full rep:flex-col rep:bg-card'>
      <PalmettoDataGrid
        key={`${domain}-${d.organizationId ?? 'all'}`}
        title='Manage Notifications'
        hideSearch
        headerIcon={
          <IconButton aria-label='Back' onDark onClick={() => navigate(d.config.backPath)}>
            <ArrowLeft className='rep:text-white' />
          </IconButton>
        }
        dataset={d.rows as unknown as Record<string, unknown>[]}
        loading={d.listLoading}
        columns={COLUMNS}
        idField='pvDataID'
        defaultSort={[{ field: 'pvFullName', sort: 'asc' }]}
        hideFilter
        components={{ MobileRow }}
        mobileTableProps={{ rowHeight: 128, backgroundColor: MOBILE_CARD_BAND }}
        onRowClick={(params) => navigate(d.viewPath(Number(params.id)))}
      />
      {canWrite ? (
        <Fab label='Add Notification' bottom={80} onClick={() => navigate(d.newPath)} />
      ) : null}
    </div>
  );
};

export default NotificationsList;
